import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

// Attach JWT from localStorage on every request.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("dataeko_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Capture the server-issued sliding refresh token (X-Refresh-Token) on every successful
// response, and globally handle 401 (expired/invalid token) by clearing state and redirecting.
api.interceptors.response.use(
  (res) => {
    const fresh = res.headers?.["x-refresh-token"];
    if (fresh) {
      localStorage.setItem("dataeko_token", fresh);
    }
    return res;
  },
  (err) => {
    // Only auto-redirect to login on 401 for "regular" API calls (not auth endpoints).
    // Auth endpoints surface their own errors to their callers (login forms, AuthCallback, etc).
    const url = err?.config?.url || "";
    const isAuthCall = url.includes("/auth/");
    if (err?.response?.status === 401 && !isAuthCall) {
      const onLogin = typeof window !== "undefined" && window.location.pathname.startsWith("/login");
      if (!onLogin) {
        localStorage.removeItem("dataeko_token");
        if (typeof window !== "undefined") {
          window.location.replace("/login?session=expired");
        }
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export const STATUS_META = {
  P:    { label: "Present (Full Day)",       short: "Present",     color: "#22C55E", bg: "rgba(34,197,94,0.18)",   border: "#16A34A", half: false, group: "present" },
  PH:   { label: "Present (Half Day)",       short: "Half Day",    color: "#A3E635", bg: "rgba(163,230,53,0.18)",  border: "#84CC16", half: true,  group: "present" },
  WFH:  { label: "WFH (Approved – Full)",    short: "WFH",         color: "#38BDF8", bg: "rgba(56,189,248,0.18)",  border: "#0284C7", half: false, group: "wfh" },
  WFHH: { label: "WFH (Approved – Half)",    short: "WFH Half",    color: "#7DD3FC", bg: "rgba(125,211,252,0.18)", border: "#0284C7", half: true,  group: "wfh" },
  PTO:  { label: "PTO / Leave (Full Day)",   short: "PTO",         color: "#FB923C", bg: "rgba(251,146,60,0.18)",  border: "#EA580C", half: false, group: "pto" },
  PTOH: { label: "PTO / Leave (Half Day)",   short: "PTO Half",    color: "#FDBA74", bg: "rgba(253,186,116,0.18)", border: "#EA580C", half: true,  group: "pto" },
  LOP:  { label: "LOP (Loss of Pay)",        short: "LOP",         color: "#F472B6", bg: "rgba(244,114,182,0.18)", border: "#DB2777", half: false, group: "lop" },
  LOPH: { label: "LOP (Half Day)",           short: "LOP Half",    color: "#F9A8D4", bg: "rgba(249,168,212,0.18)", border: "#DB2777", half: true,  group: "lop" },
  WO:   { label: "Week Off",                 short: "Week Off",    color: "#94A3B8", bg: "rgba(148,163,184,0.18)", border: "#64748B", half: false, group: "wo" },
  HO:   { label: "Holiday (Company)",        short: "Holiday",     color: "#FBBF24", bg: "rgba(251,191,36,0.18)",  border: "#D97706", half: false, group: "ho" },
};

export const STATUS_CODES = Object.keys(STATUS_META);
export const FULL_DAY_CODES = ["P", "WFH", "PTO", "LOP", "WO"];
export const HALF_DAY_CODES = ["PH", "WFHH", "PTOH", "LOPH"];

// Weight map for client-side working-day computation (mirrors backend).
export const WEIGHT = { P: 1, PH: 0.5, WFH: 1, WFHH: 0.5, PTO: 0, PTOH: 0, LOP: 0, LOPH: 0, WO: 0, HO: 0 };

export function parseEntry(v) {
  if (!v) return [];
  return v.split("+").map(s => s.trim()).filter(Boolean);
}
export function entryWeight(v) {
  const parts = parseEntry(v);
  return Math.min(1, parts.reduce((s, c) => s + (WEIGHT[c] || 0), 0));
}

export const APPROVAL_STATUS = {
  draft: { label: "Draft", color: "#64748B" },
  submitted: { label: "Submitted", color: "#3B82F6" },
  approved: { label: "Approved", color: "#10B981" },
  clarification: { label: "Clarification", color: "#F59E0B" },
};
